import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Receipt, ExternalLink } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { StatusBadge } from '../../components/StatusBadge';
import { KpiGrid, SectionHeader, ExportButtons, ReportTable, fIDR, fIDRCompact, fDateID } from './shared';

export function InvoicesTab({ data, loading, filters }) {
  const navigate = useNavigate();
  const s = data?.summary || {};
  const cards = [
    { label: 'Jumlah Invoice', value: s.invoice_count || 0, accent: 'primary' },
    { label: 'Total DPP', value: fIDRCompact(s.total_dpp), accent: '' },
    { label: 'Total PPN', value: fIDRCompact(s.total_ppn), accent: 'primary' },
    { label: 'Grand Total', value: fIDRCompact(s.total_grand), accent: '' },
    { label: 'Total PPh', value: fIDRCompact(s.total_pph), accent: 'warn' },
    { label: 'Net Collect', value: fIDRCompact(s.total_net_collect), accent: '' },
    { label: 'Total Terbayar', value: fIDRCompact(s.total_paid), accent: 'success' },
    { label: 'Outstanding', value: fIDRCompact(s.total_outstanding), accent: 'warn' },
  ];
  const rows = data?.rows || [];
  return (
    <div className="space-y-4">
      <SectionHeader icon={Receipt} title="Laporan Invoice" action={<ExportButtons tab="invoices" filters={filters} />} />
      <KpiGrid items={cards} loading={loading} columns={4} />
      <Card className="bg-card border-border">
        <CardHeader className="pb-2"><CardTitle className="text-sm text-slate-100">Detail Invoice ({rows.length})</CardTitle></CardHeader>
        <CardContent className="p-0 sm:p-4">
          <ReportTable
            getRowKey={(r) => r.id}
            columns={[
              { key: 'invoice_number', label: 'No Invoice', render: r => <span className="font-mono-num text-slate-100">{r.invoice_number}</span> },
              { key: 'invoice_date', label: 'Tgl Invoice', render: r => <span className="text-slate-300">{fDateID(r.invoice_date)}</span> },
              { key: 'event_date', label: 'Tgl Event', render: r => <span className="text-slate-300">{fDateID(r.event_date)}</span> },
              { key: 'client_name', label: 'Client', render: r => <span className="font-medium text-slate-100">{r.client_name}</span> },
              { key: 'venue', label: 'Venue', render: r => <span className="text-slate-400 truncate" title={r.venue}>{r.venue || '—'}</span> },
              { key: 'dpp_amount', label: 'DPP', align: 'right', render: r => <span className="font-mono-num text-slate-200">{fIDR(r.dpp_amount)}</span> },
              { key: 'dpp_nilai_lain_amount', label: 'DPP Nilai Lain', align: 'right', render: r => <span className="font-mono-num text-slate-300">{fIDR(r.dpp_nilai_lain_amount)}</span> },
              { key: 'ppn_amount', label: 'PPN', align: 'right', render: r => <span className="font-mono-num text-primary">{fIDR(r.ppn_amount)}</span> },
              { key: 'grand_total_amount', label: 'Grand Total', align: 'right', render: r => <span className="font-mono-num text-slate-100">{fIDR(r.grand_total_amount)}</span> },
              { key: 'withholding_tax_amount', label: 'PPh', align: 'right', render: r => <span className="font-mono-num text-amber-300">{fIDR(r.withholding_tax_amount)}</span> },
              { key: 'net_amount_to_collect', label: 'Net Collect', align: 'right', render: r => <span className="font-mono-num text-slate-100">{fIDR(r.net_amount_to_collect)}</span> },
              { key: 'paid_amount', label: 'Paid', align: 'right', render: r => <span className="font-mono-num text-emerald-300">{fIDR(r.paid_amount)}</span> },
              { key: 'outstanding_amount', label: 'Out', align: 'right', render: r => <span className="font-mono-num text-amber-200">{fIDR(r.outstanding_amount)}</span> },
              { key: 'status', label: 'Status', render: r => <StatusBadge status={r.status} /> },
              { key: 'withholding_status', label: 'Bukti Pot.', render: r => <span className="text-xs text-slate-300">{r.withholding_status === 'Not Required' ? '—' : r.withholding_status}</span> },
              { key: 'has_proof', label: 'Bukti TF', render: r => <span className={`text-xs ${r.has_proof ? 'text-emerald-300' : 'text-slate-400'}`}>{r.has_proof ? 'Ada' : 'Belum'}</span> },
              { key: 'action', label: 'Aksi', align: 'right', render: r => (
                <Button size="sm" variant="secondary" className="h-7 px-2 text-xs" onClick={(e) => { e.stopPropagation(); navigate(`/invoices/${r.id}`); }} data-testid={`report-invoice-detail-${r.id}`}>
                  <ExternalLink className="h-3 w-3" />
                </Button>
              ) },
            ]}
            rows={rows}
            emptyText="Tidak ada invoice pada periode ini."
            mobileCard={(r) => (
              <div className="text-sm space-y-1" onClick={() => navigate(`/invoices/${r.id}`)}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-slate-100 truncate">{r.client_name}</div>
                    <div className="text-xs text-slate-300 font-mono-num">{r.invoice_number}</div>
                  </div>
                  <StatusBadge status={r.status} />
                </div>
                <div className="grid grid-cols-2 gap-1 text-xs text-slate-300 mt-2">
                  <div>Tgl: {fDateID(r.invoice_date)}</div>
                  <div>Venue: <span className="truncate inline-block max-w-[120px] align-bottom">{r.venue || '—'}</span></div>
                  <div>DPP: <span className="font-mono-num text-slate-100">{fIDR(r.dpp_amount)}</span></div>
                  <div>PPN: <span className="font-mono-num text-primary">{fIDR(r.ppn_amount)}</span></div>
                  <div>Grand: <span className="font-mono-num text-slate-100">{fIDR(r.grand_total_amount)}</span></div>
                  <div>Net: <span className="font-mono-num text-slate-100">{fIDR(r.net_amount_to_collect)}</span></div>
                  <div>Paid: <span className="font-mono-num text-emerald-300">{fIDR(r.paid_amount)}</span></div>
                  <div>Out: <span className="font-mono-num text-amber-200">{fIDR(r.outstanding_amount)}</span></div>
                </div>
              </div>
            )}
          />
        </CardContent>
      </Card>
    </div>
  );
}
